import React, { useEffect, useMemo, useState } from "react";
import styles from "../../../../styles/scope3/Dashboard/purchaseGoods.module.css";
import BarChart from "../../../../Components/GraphNCharts/BarChart";
import EchartDoughnutChart from "../../../../Components/GraphNCharts/EchartDoughnut";
import PeriodFilter from "../../../../Components/Common/PeriodFilter";
import {
  formatDashboardEmissionValue,
  formatNumberScientificCompact,
  formatTruncatedDecimalPlaces,
} from "../../../../utils/common";
import { Box } from "@mui/material";
import useAuthStore from "../../../../store/ContextUser";
import { get } from "../../../../utils/api";

const Dashboard = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const { selectedCompany } = useAuthStore();

  const [selectedFacility, setSelectedFacility] = useState([]);
  const [fromDate, setFromDate] = useState({
    month: 1,
    year: currentYear,
  });
  const [toDate, setToDate] = useState({
    month: currentDate.getMonth() + 1,
    year: currentYear,
  });

  const [dashboardData, setDashboardData] = useState({
    emission: {
      totalLeasedAreaUnits: 0,
      totalEmissions: 0,
      totalElectricityConsumption: 0,
      emissionIntensity: 0,
    },
    emissionByAssetType: { labels: [], dataPoints: [] },
    topAssets: { labels: [], dataPoints: [] },
  });

  const apiurls = useMemo(
    () => ({
      emission: `/dashboard/scope3/streamleased/totalUpstreamLeasedEmission?startYear=${fromDate.year}&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${toDate.month}&facilities=${selectedFacility.join(",")}&companyId=${selectedCompany?._id || ""}`,
      emissionByCatoryAndCountry: `/dashboard/scope3/streamleased/upstreamLeasedPageData?startYear=${fromDate.year}&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${toDate.month}&facilities=${selectedFacility.join(",")}&companyId=${selectedCompany?._id || ""}`,
    }),
    [fromDate, toDate, selectedFacility, selectedCompany],
  );

  const normalizeEmission = (payload) => {
    const data = payload?.data || payload || {};
    return {
      totalLeasedAreaUnits: Number(data?.totalLeasedAreaUnits || 0),
      totalEmissions: Number(data?.totalEmissions || 0),
      totalElectricityConsumption: Number(
        data?.totalElectricityConsumption || 0,
      ),
      emissionIntensity:
        data?.emissionIntensity == null
          ? 0
          : Number(data?.emissionIntensity || 0),
    };
  };

  const formatCardMetric = (value) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return "0";
    if (n === 0) return "0";
    const abs = Math.abs(n);
    if (abs > 0 && abs < 0.1) {
      return formatNumberScientificCompact(n, 2);
    }
    return formatTruncatedDecimalPlaces(n, 3);
  };

  const fetchData = async () => {
    try {
      const [emissionRes, emissionData] = await Promise.all([
        get(apiurls.emission),
        get(apiurls.emissionByCatoryAndCountry),
      ]);

      const emission = normalizeEmission(emissionRes?.data);

      const assetTypeData = emissionData?.data?.emissionByAssetType || [];
      const topAssetsData =
        emissionData?.data?.top5EmissionContributingAssets || [];

      const emissionByAssetType = assetTypeData.filter(
        (item) => item?.name && Number(item?.totalEmission) > 0,
      );
      const topAssets = topAssetsData.filter(
        (item) => item?.name && Number(item?.totalEmission) >= 0,
      );

      setDashboardData({
        emission,
        emissionByAssetType: {
          labels: emissionByAssetType.map((item) => item.name),
          dataPoints: emissionByAssetType.map((item) =>
            Number(item.totalEmission || 0),
          ),
        },
        topAssets: {
          labels: topAssets.map((item) => item.name),
          dataPoints: topAssets.map((item) => Number(item.totalEmission || 0)),
        },
      });
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    if (!selectedCompany?._id) return;
    fetchData();
  }, [fromDate, toDate, selectedFacility, selectedCompany?._id]);

  const generateYearList = (startYear = 2020) => {
    const years = [];
    for (let year = startYear; year <= currentYear; year++) years.push(year);
    return years;
  };

  const yearList = generateYearList(2020);

  return (
    <>
      <div className={styles.heading_container}>
        <p className={styles.heading}>Upstream Leased Assets</p>
        <PeriodFilter
          {...{
            fromDate,
            setFromDate,
            toDate,
            setToDate,
            yearList,
            setSelectedFacility,
            selectedFacility,
          }}
        />
      </div>

      <div className={styles.purchase_container}>
        <p className={styles.sub_heading}>Emission Analysis</p>

        <div className={styles.emission_container}>
          <div className={styles.card_container}>
            <p className={styles.graph_heading}>Total Leased Area/Units</p>
            <p className={styles.card_value}>
              {formatCardMetric(dashboardData.emission.totalLeasedAreaUnits)}
            </p>
          </div>

          <div className={styles.card_container}>
            <p className={styles.graph_heading}>Total Fuel Emissions</p>
            <p className={styles.card_value}>
              {formatCardMetric(dashboardData.emission.totalEmissions)} tCO₂e
            </p>
          </div>
        </div>

        <div style={{ display: "flex", gap: "1.2rem", height:"100%" ,}}>
          <div
            className={styles.emission_container, styles.chart_container}
            style={{ width: "calc(50% - 0.6rem)",  }}
          >
              <p className={styles.graph_heading}>Emission by asset type</p>
            <Box sx={{ width: "45%", minHeight: "220px" , display:"flex"}}>
              <EchartDoughnutChart
                labels={dashboardData?.emissionByAssetType?.labels || []}
                dataPoints={
                  dashboardData?.emissionByAssetType?.dataPoints || []
                }
                colors={["#0052CC", "#7B2CBF", "#FF3D3D", "#37C837", "#FFB000"]}
                customHeight="220px"
                formatDisplayValue={formatDashboardEmissionValue}
              />
              <div className={styles.doughnut_table_div}>
              {(dashboardData?.emissionByAssetType?.labels || []).map(
                (label, idx) => (
                  <div className={styles.table_container} key={label}>
                    <div className={styles.table_row}>
                      <span
                        className={styles.table_row_label}
                        style={{
                          backgroundColor: [
                            "#0052CC",
                            "#7B2CBF",
                            "#FF3D3D",
                            "#37C837",
                            "#FFB000",
                          ][idx],
                        }}
                      />
                      <span className={styles.table_text}>{label}</span>
                    </div>
                    <span className={styles.table_text_val}>
                      {formatDashboardEmissionValue(
                        dashboardData?.emissionByAssetType?.dataPoints?.[idx] ??
                          0,
                      )}
                    </span>
                  </div>
                ),
              )}
            </div>
            </Box>
            
          </div>
          <div
            className={styles.emission_container}
            style={{
              display: "flex",
              flexDirection: "column",
              gap:"1.2rem",
               width: "calc(50% - 0.6rem)",
              //  border:"2px solid red"
            }}
          >
            <div className={styles.card_container} style={{ width: "100%", height:"calc(50% - 0.6rem)" }}>
              <p className={styles.graph_heading}>
                Total Electricity Consumption
              </p>
              <p className={styles.card_value}>
                {formatCardMetric(
                  dashboardData.emission.totalElectricityConsumption,
                )}{" "}
                tCO₂e
              </p>
            </div>

            <div className={styles.card_container} style={{ width: "100%",height:"calc(50% - 0.6rem)" }}>
              <p className={styles.graph_heading}>
                Emission Intensity (Total emission/Total Area or unit)
              </p>
              <p className={styles.card_value}>
                {formatCardMetric(dashboardData.emission.emissionIntensity)}
              </p>
            </div>
          </div>
        </div>

        <div
          className={styles.chart_container}
          style={{ width: "calc(100% - 0.6rem)" }}
        >
          <div className={styles.graph_container_header}>
            <p
              className={`${styles.graph_heading} ${styles.graph_heading_bar_ref}`}
            >
              Top 5 Emission-Contributing Assets
            </p>
          </div>
          <BarChart
            labels={dashboardData?.topAssets?.labels || []}
            datasets={[
              {
                label: "Emissions",
                data: dashboardData?.topAssets?.dataPoints || [],
                backgroundColor: "rgba(59, 130, 246, 0.95)",
                borderColor: "rgba(59, 130, 246, 1)",
                borderWidth: 0,
              },
            ]}
            customHeight="260px"
            ellipsis={false}
            linearTickFormatter={formatDashboardEmissionValue}
            horizontalCleanStyle
            valueAxisTitle="Emissions"
          />
        </div>
      </div>
    </>
  );
};

export default Dashboard;
